// client/src/components/indicadores/ComissoesReport.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api'; // ✅ USANDO API PADRONIZADA
import useAlert from '../../hooks/useAlert';
import { useAuth } from '../../context/auth/AuthProvider';
import { useComissoesReport, useComissoesDetalhe, useComissoesPdf } from '../../hooks/useReports';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency } from '../../utils/formatters';
import { Percent, DollarSign, Users, Download, Loader2 } from 'lucide-react';
import ReportPageLayout from './ReportPageLayout';
import ReportTable from './ReportTable';
import ComissoesDetalhePanel from './ComissoesDetalhePanel';

// ✅ DEFINIR FILTROS INICIAIS COMO CONSTANTE — ampliado com os filtros novos
// (unidade/serviço/recurso/produto), antes só tinha startDate/endDate e por
// isso sobrescrevia o default mais completo de ReportPageLayout.
const initialFilterState = {
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date(),
    unidadeId: '',
    servicoId: '',
    recursoId: '',
    produtoId: '',
};

const ComissoesReport = () => {
    const { setAlert } = useAlert();
    const { user, scope } = useAuth();
    // ✅ ACHADO: relatorio fixo em BRL, sem ler configuracoesGerais.i18n
    // da conta.
    const { currency, locale } = useTenantI18n();

    const [appliedFilters, setAppliedFilters] = useState(initialFilterState);
    const [filterOptions, setFilterOptions] = useState({
        unidades: [],
        profissionais: [],
        servicos: [],
        recursos: [],
        produtos: [],
    });
    const [loadingFilters, setLoadingFilters] = useState(false);

    // ✅ NOVO: profissional clicado na tabela — abre o painel de
    // detalhamento inline abaixo dela. Clicar de novo na mesma linha
    // fecha; clicar em outra troca o painel.
    const [expandedProfissional, setExpandedProfissional] = useState(null); // { id, nome } | null
    const { baixarPdf } = useComissoesPdf();
    const [baixandoPdf, setBaixandoPdf] = useState(false);

    // 🔥 USANDO NOVO HOOK - useComissoesReport
    const {
        data: reportData = [],
        isLoading,
        error
    } = useComissoesReport(appliedFilters);

    const { data: detalheData, isLoading: isLoadingDetalhe } = useComissoesDetalhe(
        expandedProfissional?.id || null,
        appliedFilters
    );

    // ✅ TRATAMENTO DE ERRO PADRONIZADO
    useEffect(() => {
        if (error) {
            console.error('❌ Erro no relatório de comissões:', error);

            let errorMessage = 'Ocorreu um erro ao buscar o relatório de comissões.';

            if (error.message.includes('Servidor indisponível')) {
                errorMessage = error.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }

            setAlert(errorMessage, 'danger');
        }
    }, [error, setAlert]);

    // 🔥 CARREGAR OPÇÕES DE FILTRO - PADRONIZADO
    useEffect(() => {
        const fetchFilterOptions = async () => {
            // ✅ CORREÇÃO: só bloqueava sem user.account — franquia_admin e
            // matriz_admin nunca viam essas opções de filtro. Os endpoints
            // já escopam sozinhos pelo JWT (filterByUserUnits).
            if (!user?.account?._id && !user?.franquia?._id && scope !== 'matriz') {
                console.log('⚠️ Usuário sem conta, franquia ou escopo matriz — sem opções de filtro');
                return;
            }

            setLoadingFilters(true);

            try {
                const accountId = user?.account?._id;
                const baseParams = accountId ? { account: accountId, limit: 1000 } : { limit: 1000 };
                console.log('🔄 Carregando filtros de comissões', baseParams);

                // ✅ PADRONIZADO: Usando a mesma estrutura dos outros componentes
                const requests = [
                    // Unidades
                    api.get('/unidades', { params: baseParams }),
                    // Profissionais
                    api.get('/users', { params: { ...baseParams, role: 'profissional' } }),
                    // Serviços
                    api.get('/servicos', { params: baseParams }),
                    // Recursos
                    api.get('/recursos', { params: baseParams }),
                    // Produtos (ItemVenda)
                    api.get('/itens-venda', { params: baseParams })
                ];

                console.log('📡 Fazendo requisições para filtros...');

                const [unidadesRes, profissionaisRes, servicosRes, recursosRes, produtosRes] = await Promise.all(requests);

                // ✅ PADRONIZADO: Mesma estrutura de extração de dados
                const filterData = {
                    unidades: unidadesRes.data?.data || unidadesRes.data || [],
                    profissionais: profissionaisRes.data?.data || profissionaisRes.data || [],
                    servicos: servicosRes.data?.data || servicosRes.data || [],
                    recursos: recursosRes.data?.data || recursosRes.data || [],
                    produtos: produtosRes.data?.data || produtosRes.data || [],
                };

                setFilterOptions(filterData);

                console.log('✅ Filtros de comissões carregados com sucesso:', {
                    unidades: filterData.unidades.length,
                    profissionais: filterData.profissionais.length,
                    servicos: filterData.servicos.length,
                    recursos: filterData.recursos.length,
                    produtos: filterData.produtos.length
                });

            } catch (error) {
                console.error('❌ Erro ao carregar filtros de comissões:', error);

                let errorMessage = 'Erro ao carregar opções de filtro.';

                if (error.message.includes('Servidor indisponível')) {
                    errorMessage = 'Servidor backend indisponível. Verifique se está rodando.';
                } else if (error.response?.data?.error) {
                    errorMessage = error.response.data.error;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                setAlert(errorMessage, 'danger');

                // ✅ FALLBACK: Definir arrays vazios para evitar quebra da UI
                setFilterOptions({
                    unidades: [],
                    profissionais: [],
                    servicos: [],
                    recursos: [],
                    produtos: []
                });
            } finally {
                setLoadingFilters(false);
            }
        };

        fetchFilterOptions();
    }, [user, scope, setAlert]);

    const handleRowClick = (row) => {
        if (!row._id) return; // "Profissional não informado" — sem id pra detalhar
        setExpandedProfissional((prev) => (prev?.id === row._id ? null : { id: row._id, nome: row.profissional?.name || 'Profissional' }));
    };

    const handleBaixarPdf = async () => {
        setBaixandoPdf(true);
        try {
            await baixarPdf(appliedFilters);
        } catch (err) {
            console.error('❌ Erro ao gerar PDF de comissões:', err);
            setAlert(err.response?.data?.error || err.message || 'Erro ao gerar o PDF do relatório.', 'danger');
        } finally {
            setBaixandoPdf(false);
        }
    };

    // 🔥 COLUNAS DA TABELA
    const columns = [
        {
            key: 'profissional',
            header: 'Profissional',
            render: (row) => row.profissional?.name || 'N/A'
        },
        {
            key: 'totalComissoes',
            header: 'Total de Comissões',
            render: (row) => formatCurrency(row.totalComissoes || 0, currency, locale)
        },
        {
            key: 'totalVendas',
            header: 'Total em Vendas',
            render: (row) => formatCurrency(row.totalVendas || 0, currency, locale)
        },
        {
            key: 'percentual',
            header: 'Percentual',
            render: (row) => `${((row.totalComissoes / row.totalVendas) * 100 || 0).toFixed(1)}%`
        },
    ];

    // ✅ VERIFICAÇÃO DE CONEXÃO COM BACKEND
    const isBackendUnavailable = error?.message?.includes('Servidor indisponível');

    // ✅ KPIs DINÂMICOS
    const kpiCards = [
        {
            title: "Total de Profissionais",
            value: (reportData?.length || 0).toLocaleString('pt-BR'),
            prefix: "",
            icon: Users,
            description: "Profissionais com comissões"
        },
        {
            title: "Comissões Totais",
            value: formatCurrency(reportData?.reduce((sum, item) => sum + (item.totalComissoes || 0), 0) || 0, currency, locale),
            prefix: "",
            icon: DollarSign,
            description: "Valor total em comissões"
        },
        {
            title: "Vendas Totais",
            value: formatCurrency(reportData?.reduce((sum, item) => sum + (item.totalVendas || 0), 0) || 0, currency, locale),
            prefix: "",
            icon: DollarSign,
            description: "Valor total em vendas"
        },
        {
            title: "Percentual Médio",
            value: (reportData?.length > 0
                ? (reportData.reduce((sum, item) => {
                    const percent = (item.totalComissoes / item.totalVendas) * 100 || 0;
                    return sum + percent;
                }, 0) / reportData.length).toFixed(1)
                : 0
            ) + "%",
            prefix: "",
            icon: Percent,
            description: "Percentual médio de comissão"
        }
    ];

    return (
        <ReportPageLayout
            icon={<Percent size={24} />}
            title="Relatório de Comissões"
            onApplyFilters={setAppliedFilters}
            filterOptions={filterOptions}
            enabledFilters={['date', 'unidade', 'profissional', 'servico', 'recurso', 'produto']}
            isLoading={isLoading || loadingFilters}
            initialFilters={initialFilterState}
            kpiCards={kpiCards}
            extraActions={
                <button
                    onClick={handleBaixarPdf}
                    className="action-button action-button-secondary"
                    disabled={baixandoPdf || isLoading}
                >
                    {baixandoPdf ? <Loader2 size={18} className="icon animate-spin" /> : <Download size={18} className="icon" />}
                    Baixar PDF
                </button>
            }
        >
            {/* 🔥 MENSAGEM DE ERRO DE CONEXÃO - PRIORIDADE MÁXIMA */}
            {isBackendUnavailable && (
                <div className="content-panel mb-4">
                    <div className="alert alert-danger">
                        <div className="d-flex align-items-center">
                            <div className="flex-grow-1">
                                <h5>❌ Erro de Conexão com o Servidor</h5>
                                <p className="mb-2">{error.message}</p>
                                <small className="text-muted">
                                    <strong>Para resolver:</strong><br/>
                                    1. Verifique se o servidor backend está rodando<br/>
                                    2. Confirme se a API está respondendo<br/>
                                    3. Verifique o comando: <code>npm run dev</code> no backend<br/>
                                    4. Recarregue a página após iniciar o servidor
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ✅ CORREÇÃO: esta tela tratava QUALQUER resultado vazio (ex.:
                período genuinamente sem comissão nenhuma) como "recurso não
                implementado" e mostrava uma prévia falsa — o backend
                (buildComissoesReport) já foi corrigido faz tempo pra
                agregar dados reais, mas o frontend nunca foi atualizado.
                ReportTable já trata loading e vazio corretamente sozinho. */}
            {!isBackendUnavailable && (
                <>
                    <ReportTable
                        title="Distribuição de Comissões por Profissional (toque numa linha pra ver o detalhamento)"
                        columns={columns}
                        data={reportData}
                        isLoading={isLoading}
                        onRowClick={handleRowClick}
                    />
                    {expandedProfissional && (
                        <div style={{ marginTop: '1rem' }}>
                            <ComissoesDetalhePanel
                                profissionalNome={expandedProfissional.nome}
                                itens={detalheData?.itens || []}
                                resumo={detalheData?.resumo}
                                isLoading={isLoadingDetalhe}
                                currency={currency}
                                locale={locale}
                            />
                        </div>
                    )}
                </>
            )}
        </ReportPageLayout>
    );
};

export default ComissoesReport;
